import { AllProjectsDocument, CreateProjectInput, useCreateProjectMutation } from "@gql/sites-api/graphql";
import { LandTechEndpoints, routedClient } from "react-migration/lib/persistence/apollo";

import { ApolloError } from "@apollo/client";
import { Icon } from "react-migration/components/Icon";
import { PROJECT_TITLE_ALREADY_EXISTS_CODE } from "../ProjectModal/constants";
import { ProjectModal } from "../ProjectModal/ProjectModal";
import { setSelectedProjectId } from "src/js/stores/site/actions/setSelectedProject";
import { useCallback } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

type NewProjectModalProps = {
  onClose: () => void;
};

export const NewProjectModal = ({ onClose }: NewProjectModalProps) => {
  const { t } = useTranslation();

  const [createProject, { loading, error, reset }] = useCreateProjectMutation({
    client: routedClient,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    refetchQueries: [AllProjectsDocument],
    onCompleted: ({ createProject }) => {
      setSelectedProjectId(createProject.id);
    },
  });

  const handleCreateProject = useCallback(
    async (input: CreateProjectInput) => {
      try {
        const { errors } = await createProject({
          variables: { input },
        });

        if (!errors?.length) {
          onClose();
        }
      } catch (error) {
        console.error("Failed to create project:", error);
      }
    },
    [createProject, onClose]
  );

  const errorMessage = getTitleErrorMessage(error);
  const hasGenericError = !!error && !errorMessage;

  return (
    <ProjectModal
      title={<NewProjectModalTitle />}
      actionLabel={t("projects.modal.create_project")}
      handleAction={handleCreateProject}
      onClose={onClose}
      loading={loading}
      titleError={errorMessage}
      genericError={hasGenericError}
      onErrorDismiss={reset}
    />
  );
};

const NewProjectModalTitle = () => {
  const { t } = useTranslation();
  return (
    <span className="flex items-center text-lg font-semibold gap-x-2">
      <Icon icon="icon-lt-folder-add-line" size="lg" />
      <span>{t("projects.modal.new_project")}</span>
    </span>
  );
};

function getTitleErrorMessage(error?: ApolloError) {
  const titleError = error?.graphQLErrors?.find(
    (error) => error.extensions?.code === PROJECT_TITLE_ALREADY_EXISTS_CODE
  );
  return titleError?.message;
}
